import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ClockIcon,
  UserIcon,
  GlobeAltIcon,
  ServerIcon,
  CpuChipIcon,
  ShieldExclamationIcon,
  DocumentDuplicateIcon,
  MapPinIcon,
  TagIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  WifiIcon,
  CodeBracketIcon,
} from '@heroicons/react/24/outline';
import ConnectionContextPanel from './ConnectionContextPanel';

// Category configuration
const categoryConfig = {
  ssh: { icon: ServerIcon, color: 'bg-purple-100 text-purple-700', label: 'SSH' },
  employee: { icon: UserIcon, color: 'bg-blue-100 text-blue-700', label: 'Employee' },
  user: { icon: UserIcon, color: 'bg-cyan-100 text-cyan-700', label: 'User' },
  device: { icon: CpuChipIcon, color: 'bg-green-100 text-green-700', label: 'Device' },
  api: { icon: GlobeAltIcon, color: 'bg-orange-100 text-orange-700', label: 'API' },
  ws: { icon: ServerIcon, color: 'bg-indigo-100 text-indigo-700', label: 'WebSocket' },
  security: { icon: ShieldExclamationIcon, color: 'bg-red-100 text-red-700', label: 'Security' },
  system: { icon: ServerIcon, color: 'bg-gray-100 text-gray-700', label: 'System' },
};

// Level configuration
const levelConfig = {
  audit: { icon: InformationCircleIcon, color: 'text-blue-600', bg: 'bg-blue-100' },
  info: { icon: CheckCircleIcon, color: 'text-green-600', bg: 'bg-green-100' },
  warn: { icon: ExclamationTriangleIcon, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  error: { icon: XMarkIcon, color: 'text-red-600', bg: 'bg-red-100' },
  debug: { icon: InformationCircleIcon, color: 'text-gray-600', bg: 'bg-gray-100' },
};

// Info row component
function InfoRow({ icon, label, value, mono = false, copyable = false }) {
  if (!value) return null;
  const Icon = icon;

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
  };

  return (
    <div className="flex items-start gap-3 py-2">
      <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 mb-0.5">{label}</p>
        <div className="flex items-center gap-2">
          <p className={`text-sm text-gray-900 ${mono ? 'font-mono' : ''} break-all`}>
            {value}
          </p>
          {copyable && (
            <button
              onClick={handleCopy}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title="Copy to clipboard"
            >
              <DocumentDuplicateIcon className="w-4 h-4 text-gray-400" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Section component
function Section({ title, children, className = '' }) {
  return (
    <div className={`border-b border-gray-100 pb-4 mb-4 last:border-0 last:mb-0 last:pb-0 ${className}`}>
      <h4 className="text-sm font-semibold text-gray-700 mb-3">{title}</h4>
      {children}
    </div>
  );
}

export default function LogDetailModal({ log, isOpen, onClose }) {
  if (!log) return null;

  const category = categoryConfig[log.category] || categoryConfig.system;
  const level = levelConfig[log.level] || levelConfig.info;
  const CategoryIcon = category.icon;
  const LevelIcon = level.icon;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all">
                {/* Header */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${category.color.split(' ')[0]}`}>
                        <CategoryIcon className={`w-5 h-5 ${category.color.split(' ')[1]}`} />
                      </div>
                      <div>
                        <Dialog.Title className="text-lg font-semibold text-gray-900">
                          {log.action || 'Log Details'}
                        </Dialog.Title>
                        <p className="text-sm text-gray-500">
                          {new Date(log.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  {/* Badges */}
                  <div className="flex items-center gap-2 mt-3">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${category.color}`}>
                      <CategoryIcon className="w-3.5 h-3.5" />
                      {category.label}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${level.bg} ${level.color}`}>
                      <LevelIcon className="w-3.5 h-3.5" />
                      {log.level?.toUpperCase()}
                    </span>
                    {log.source && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-600">
                        <ServerIcon className="w-3.5 h-3.5" />
                        {log.source}
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                  {/* Message */}
                  {log.message && (
                    <Section title="Message">
                      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-800">
                        {log.message}
                      </div>
                    </Section>
                  )}

                  {/* Timing */}
                  <Section title="Timing">
                    <div className="grid grid-cols-2 gap-4">
                      <InfoRow
                        icon={ClockIcon}
                        label="Timestamp"
                        value={new Date(log.timestamp).toLocaleString()}
                      />
                      {log.outcome?.duration && (
                        <InfoRow
                          icon={ClockIcon}
                          label="Duration"
                          value={`${log.outcome.duration}ms`}
                        />
                      )}
                    </div>
                  </Section>

                  {/* Actor Information */}
                  {log.actor && (
                    <Section title="Actor">
                      <div className="grid grid-cols-2 gap-4">
                        {log.actor.email && (
                          <InfoRow icon={UserIcon} label="Email" value={log.actor.email} />
                        )}
                        {log.actor.name && (
                          <InfoRow icon={UserIcon} label="Name" value={log.actor.name} />
                        )}
                        {log.actor.role && (
                          <InfoRow icon={TagIcon} label="Role" value={log.actor.role} />
                        )}
                        {log.actor.ip && (
                          <InfoRow icon={GlobeAltIcon} label="IP Address" value={log.actor.ip} mono copyable />
                        )}
                        {log.actor.serial && (
                          <InfoRow icon={CpuChipIcon} label="Device Serial" value={log.actor.serial} mono copyable />
                        )}
                        {log.actor.type && (
                          <InfoRow icon={TagIcon} label="Actor Type" value={log.actor.type} />
                        )}
                      </div>
                    </Section>
                  )}

                  {/* Target Information */}
                  {log.target && Object.keys(log.target).length > 0 && (
                    <Section title="Target">
                      <div className="grid grid-cols-2 gap-4">
                        {log.target.type && (
                          <InfoRow icon={TagIcon} label="Type" value={log.target.type} />
                        )}
                        {log.target.serial && (
                          <InfoRow icon={CpuChipIcon} label="Device Serial" value={log.target.serial} mono copyable />
                        )}
                        {log.target.name && (
                          <InfoRow icon={TagIcon} label="Name" value={log.target.name} />
                        )}
                        {log.target.path && (
                          <InfoRow icon={GlobeAltIcon} label="Path" value={log.target.path} mono />
                        )}
                      </div>
                    </Section>
                  )}

                  {/* Geo Information */}
                  {log.geo && Object.keys(log.geo).length > 0 && (
                    <Section title="Geolocation">
                      <div className="grid grid-cols-2 gap-4">
                        {log.geo.country && (
                          <InfoRow icon={MapPinIcon} label="Country" value={log.geo.country} />
                        )}
                        {log.geo.city && (
                          <InfoRow icon={MapPinIcon} label="City" value={log.geo.city} />
                        )}
                        {log.geo.isp && (
                          <InfoRow icon={GlobeAltIcon} label="ISP" value={log.geo.isp} />
                        )}
                        {log.geo.isVPN !== undefined && (
                          <InfoRow icon={ShieldExclamationIcon} label="VPN Detected" value={log.geo.isVPN ? 'Yes' : 'No'} />
                        )}
                        {log.geo.isTor !== undefined && (
                          <InfoRow icon={ShieldExclamationIcon} label="Tor Detected" value={log.geo.isTor ? 'Yes' : 'No'} />
                        )}
                        {log.geo.threatLevel && (
                          <InfoRow icon={ShieldExclamationIcon} label="Threat Level" value={log.geo.threatLevel} />
                        )}
                      </div>
                    </Section>
                  )}

                  {/* Security Information */}
                  {log.security && Object.keys(log.security).length > 0 && (
                    <Section title="Security">
                      <div className="grid grid-cols-2 gap-4">
                        {log.security.riskScore !== undefined && (
                          <InfoRow icon={ShieldExclamationIcon} label="Risk Score" value={`${log.security.riskScore}/100`} />
                        )}
                        {log.security.alertTriggered !== undefined && (
                          <InfoRow icon={ExclamationTriangleIcon} label="Alert Triggered" value={log.security.alertTriggered ? 'Yes' : 'No'} />
                        )}
                        {log.security.isBanned !== undefined && (
                          <InfoRow icon={XMarkIcon} label="Banned" value={log.security.isBanned ? 'Yes' : 'No'} />
                        )}
                      </div>
                      {log.security.flags && log.security.flags.length > 0 && (
                        <div className="mt-3">
                          <p className="text-xs text-gray-500 mb-2">Security Flags</p>
                          <div className="flex flex-wrap gap-2">
                            {log.security.flags.map((flag, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium"
                              >
                                {flag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </Section>
                  )}

                  {/* Outcome */}
                  {log.outcome && (
                    <Section title="Outcome">
                      <div className="grid grid-cols-2 gap-4">
                        {log.outcome.status && (
                          <InfoRow
                            icon={log.outcome.status === 'success' ? CheckCircleIcon : XMarkIcon}
                            label="Status"
                            value={log.outcome.status}
                          />
                        )}
                        {log.outcome.responseSize && (
                          <InfoRow icon={TagIcon} label="Response Size" value={`${log.outcome.responseSize} bytes`} />
                        )}
                      </div>
                      {log.outcome.error && (
                        <div className="mt-3 bg-red-50 rounded-lg p-3">
                          <p className="text-xs text-red-500 mb-1">Error</p>
                          <p className="text-sm text-red-700 font-mono">
                            {log.outcome.error.message || JSON.stringify(log.outcome.error)}
                          </p>
                        </div>
                      )}
                    </Section>
                  )}

                  {/* Enhanced Connection Context Panel for WebSocket logs */}
                  {log.context && (log.context.device || log.context.geo || log.context.connection || log.context.security || log.context.account) && (
                    <Section title="">
                      <ConnectionContextPanel context={log.context} />
                    </Section>
                  )}

                  {/* Context (Raw JSON) - shown if context exists but doesn't have enhanced fields */}
                  {log.context && Object.keys(log.context).length > 0 &&
                   !log.context.device && !log.context.geo && !log.context.connection && !log.context.security && !log.context.account && (
                    <Section title="Context Data">
                      <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-xs overflow-x-auto font-mono">
                        {JSON.stringify(log.context, null, 2)}
                      </pre>
                    </Section>
                  )}

                  {/* Correlation IDs */}
                  {(log.correlationId || log.sessionId || log.requestId) && (
                    <Section title="Correlation">
                      <div className="grid grid-cols-1 gap-2">
                        {log.correlationId && (
                          <InfoRow icon={TagIcon} label="Correlation ID" value={log.correlationId} mono copyable />
                        )}
                        {log.sessionId && (
                          <InfoRow icon={TagIcon} label="Session ID" value={log.sessionId} mono copyable />
                        )}
                        {log.requestId && (
                          <InfoRow icon={TagIcon} label="Request ID" value={log.requestId} mono copyable />
                        )}
                      </div>
                    </Section>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {log._id && <span className="font-mono">ID: {log._id}</span>}
                    </div>
                    <button
                      onClick={onClose}
                      className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
